import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTransitionalCareSummary, exportTransitionalCareSummary, MenuProps } from '../WidgetService';
import { TransitionalCareDateRangeEnum } from '../../../Enum/TransitionalCareDateRangeEnum';
import { parentUrl } from '../../../utils/constants';
import { FacilityTypeEnum } from '../../../Enum/FacilityTypeEnum';
import { generateRandomId, isDefaultDate } from '../../../utils/common';
import { dateFormatter } from '../../../utils/timeFormat';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import { GridCellParams } from '@mui/x-data-grid';
import '../Widgets.css';
import { AdmissionRuleEnum } from '../../../Enum/AdmissionRuleEnum';

const TransitionalCareSummaryDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const {
        tcTeam,
        setTcTeam,
        tcFacility,
        setTcFacility,
        tcAdmissionRules,
        setTcAdmissionRules,
        tcDateFilter,
        setTcDateFilter,
        tcFromDate,
        setTcFromDate,
        tcToDate,
        setTcToDate,
        tcShowInActiveTeams,
        setTcShowInActiveTeams,
        tcTab,
        setTcTab,
    } = useWidgetContext();
    const [teamLevelTcSummary, setTeamLevelTcSummary] = useState<[]>([]);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dateFilterList = [
            { Id: TransitionalCareDateRangeEnum.Today, Value: TransitionalCareDateRangeEnum.TodayDescription },
            { Id: TransitionalCareDateRangeEnum.Last1Month, Value: TransitionalCareDateRangeEnum.Last1MonthDescription },
            { Id: TransitionalCareDateRangeEnum.Last3Months, Value: TransitionalCareDateRangeEnum.Last3MonthsDescription },
            { Id: TransitionalCareDateRangeEnum.Custom, Value: TransitionalCareDateRangeEnum.CustomDescription },
        ];

    const faclityList = [
        { Id: FacilityTypeEnum.PhysicalHealth, Value: FacilityTypeEnum.PhysicalHealthDescription },
        { Id: FacilityTypeEnum.BehavioralHealthLevel1, Value: FacilityTypeEnum.BehavioralHealthLevel1Description },
        { Id: FacilityTypeEnum.BehavioralHealthLevel2, Value: FacilityTypeEnum.BehavioralHealthLevel2Description },
    ];

    const admissionRules = [
        { label: AdmissionRuleEnum.BH7DayDescription, value: AdmissionRuleEnum.BH7Day },
        { label: AdmissionRuleEnum.PH30DayDescription, value: AdmissionRuleEnum.PH30Day }
    ];

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (tcDateFilter !== TransitionalCareDateRangeEnum.Custom || (Boolean(tcFromDate) && Boolean(tcToDate))) {
            setIsMembersLoading(true);
            const changeFilter = async () => {
                let start = tcFromDate !== null ? new Date(tcFromDate).toDateString() : null;
                let end = tcToDate !== null ? new Date(tcToDate).toDateString() : null;

                const data = await fetchTransitionalCareSummary(tcFacility, tcAdmissionRules, tcTab, tcTeam, tcDateFilter, start, end, tcShowInActiveTeams);
                setTeamLevelTcSummary(data);
                setIsMembersLoading(false);
                setIsFetched(true);
            };
            changeFilter();
        }

        if (tcDateFilter !== TransitionalCareDateRangeEnum.Custom && Boolean(tcFromDate) && Boolean(tcToDate)) {
            setTcFromDate(null);
            setTcToDate(null);
        }
    }, [tcTeam, tcFacility, tcAdmissionRules, tcTab, tcDateFilter, tcFromDate, tcToDate, tcShowInActiveTeams, setTcFromDate, setTcToDate]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const changeFromDate = (date: any) => {
        setTcFromDate(date);
    };

    const changeToDate = (date: any) => {
        setTcToDate(date);
    };

    const exportDetail = async () => {
        setDownLoading(true);
        let start = tcFromDate !== null ? new Date(tcFromDate).toDateString() : null;
        let end = tcToDate !== null ? new Date(tcToDate).toDateString() : null;
        const data = await exportTransitionalCareSummary(tcFacility, tcAdmissionRules, tcTeam, tcDateFilter, start, end, tcShowInActiveTeams);
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TransitionalCareSummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTcTab(newValue);
    };

    const getCellClass = (params: GridCellParams) => {
        if (tcTab !== 3) return '';

        const isContacted = params?.row?.IsContacted;

        if (isContacted === true) return 'green';
        if (isContacted === false) return 'red';

        return '';
    };

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Name',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                return <>{params.row.LastName} {params.row.FirstName}</>;
            },
            cellClassName: getCellClass,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
            cellClassName: getCellClass,
        },
        {
            field: 'Facility',
            headerName: 'Facility Name',
            minWidth: 300,
            flex: 1,
            cellClassName: getCellClass,
        },
        {
            field: 'AdmitDate',
            headerName: 'Admit Date',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.AdmitDate) ? '-' : dateFormatter(new Date(row.AdmitDate))}`,
            cellClassName: getCellClass,
        },
        ...(tcTab === 3
            ? [{
                field: 'DischargeDate',
                headerName: 'Discharge Date',
                minWidth: 150,
                flex: 1,
                valueGetter: (value, row) => `${isDefaultDate(row.DischargeDate) ? '-' : dateFormatter(new Date(row.DischargeDate))}`,
                cellClassName: getCellClass,
            }]
            : []),
        {
            field: 'LastCHPVisit',
            headerName: 'Last InPerson Appt Date',
            minWidth: 180,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.LastCHPVisit) ? '-' : dateFormatter(new Date(row.LastCHPVisit))}`,
            cellClassName: getCellClass,
        },
        ...(tcTab === 3
            ? [{
                field: 'IsContacted',
                headerName: 'Seen by Licensed Staff',
                minWidth: 180,
                flex: 1,
                valueGetter: (val: any) => {
                    return val === true || val === 'true' || val === 1 ? 'Yes' : 'No';
                },
                cellClassName: getCellClass,
            }]
            : [])
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Transitional Care Summary
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={1.5}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={tcTeam}
                            onChange={(id) => setTcTeam(id)}
                            showInactive={tcShowInActiveTeams}
                            onToggleInactive={setTcShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Facility</InputLabel>
                            <Select value={tcFacility} label="Facility" name="Facility" onChange={(e) => setTcFacility(e.target.value)} MenuProps={MenuProps}>
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                {faclityList.map((facility) => (
                                    <MenuItem key={facility.Id} value={facility.Id}>
                                        {facility.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Admission Rule</InputLabel>
                            <Select
                                disabled={tcTab !== 3}
                                value={tcAdmissionRules}
                                label="Admission Rule"
                                name="Admission Rule"
                                onChange={(e) => setTcAdmissionRules(e.target.value)}
                                size="small"
                                MenuProps={MenuProps}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                {admissionRules.map((rule) => (
                                    <MenuItem key={rule.value} value={rule.value}>
                                        {rule.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select disabled={tcTab === 3} value={tcDateFilter} label="Date Filter" name="Date" onChange={(e) => setTcDateFilter(parseInt(e.target.value as any))}>
                                {dateFilterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {tcDateFilter === TransitionalCareDateRangeEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(tcFromDate) ? tcFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(tcToDate) ? tcToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    <Grid size={tcDateFilter === TransitionalCareDateRangeEnum.Custom ? 1.8 : 4.8}></Grid>
                    <Grid size={1}>
                        <Button style={{ minWidth: '120px', marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                    <Grid size={12} display={'flex'}>
                        <Tabs sx={{ display: 'inline-flex', marginRight: 'auto' }} value={tcTab} onChange={handleAlertsTabChange}>
                            <Tab value={1} label="InPatient" />
                            <Tab value={3} label="Discharged" />
                            <Tab value={2} label="ED" />
                        </Tabs>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                    <Grid size={12} minHeight="65vh" sx={{ position: 'relative' }}>
                        {isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <>
                                    <Grid size={12} style={{ height: '65vh', width: '100%' }}>
                                        <DataGrid
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/transitional-care`, '_blank');
                                            }}
                                            rows={teamLevelTcSummary}
                                            columns={columns}
                                            paginationModel={{ pageSize: pageSize, page: page }}
                                            pageSizeOptions={[5]}
                                            onPaginationModelChange={(model) => {
                                                setPageSize(model.pageSize);
                                                setPage(model.page);
                                            }}
                                            getRowId={(row) => `${row.Id}-${row.AdmitDate}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                                [`& .green`]: {
                                                    color: 'green',
                                                },
                                                [`& .red`]: {
                                                    color: 'red',
                                                },
                                                [`& .yellow`]: {
                                                    color: 'gold',
                                                },
                                                [`& .black`]: {
                                                    color: 'rgba(0,0,0,0.87)',
                                                },
                                            }}
                                        />
                                    </Grid>
                                </>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default TransitionalCareSummaryDetail;
